/**
 * Auth Provider
 * 
 * Provides authentication context to the entire application.
 * Manages session state, user authentication, and auth-related operations.
 */

import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';
import { signInWithMagicLink, signOut as authSignOut, getSession, getUser } from '../services/authService';
import { signInWithTestCode as testCodeSignIn } from '../services/testCodeService';

/**
 * Authentication context value interface
 */
export interface AuthContextValue {
  /** Current authenticated user or null */
  user: User | null;
  /** Current session or null */
  session: Session | null;
  /** Whether authentication state is being loaded */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Error message if any */
  error: string | null;
  /** Sign in with email (sends magic link) */
  signIn: (email: string, captchaToken?: string) => Promise<void>;
  /** Sign in with test code */
  signInWithTestCode: (code: string, email?: string, captchaToken?: string) => Promise<void>;
  /** Sign out current user */
  signOut: () => Promise<void>;
  /** Clear current error */
  clearError: () => void;
}

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthProvider component props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider Component
 * 
 * Manages authentication state and provides auth context to children.
 * Listens to Supabase auth state changes and updates context accordingly.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Sign in with email (sends magic link)
   */
  const signIn = useCallback(async (email: string, captchaToken?: string) => {
    try {
      setError(null);
      // Don't set isLoading=true here as it causes EmailGate to unmount
      // EmailGate has its own isSubmitting state for button feedback
      await signInWithMagicLink(email, captchaToken);
      // Magic link sent - wait for user to click link
      // Auth state will update via onAuthStateChange listener
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send magic link';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Sign in with test code
   */
  const signInWithTestCode = useCallback(async (code: string, email?: string, captchaToken?: string) => {
    try {
      setError(null);
      // Don't set isLoading=true here as it causes EmailGate to unmount
      // EmailGate has its own isValidatingCode state for button feedback
      await testCodeSignIn(code, email, captchaToken);
      // Auth state will update via onAuthStateChange listener
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to authenticate with test code';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Sign out current user
   */
  const signOut = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      await authSignOut();
      setUser(null);
      setSession(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load initial session on mount
   */
  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      try {
        const [sessionData, userData] = await Promise.all([
          getSession(),
          getUser(),
        ]);

        if (mounted) {
          setSession(sessionData);
          setUser(userData);
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to load session:', err);
          setError('Failed to load authentication state');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Listen to auth state changes
   * Empty dependency array ensures single subscription for component lifetime
   */
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Update session
      setSession(session);

      // Update user
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }

      // Handle specific events
      if (event === 'SIGNED_IN') {
        setError(null);
        // Link anonymous page loads to authenticated user
        if (session?.user?.id) {
          const { linkPageLoadsToUser } = await import('../services/analyticsService');
          linkPageLoadsToUser(session.user.id).catch((err) => {
            // Silent fail - don't disrupt user experience
            if (import.meta.env.VITE_DEBUG === 'true') {
              console.error('Failed to link page loads:', err);
            }
          });
          
          // Update user profile with IP address and geolocation data
          // This ensures magic link users also get IP/geo tracking (like test code users)
          const { updateUserProfileWithGeoData } = await import('../services/authService');
          updateUserProfileWithGeoData(session.user.id).catch((err) => {
            // Silent fail - don't disrupt user experience
            if (import.meta.env.VITE_DEBUG === 'true') {
              console.error('Failed to update user profile with geo data:', err);
            }
          });
          
          // Identify user in PostHog
          const { identifyUser } = await import('../services/posthogService');
          identifyUser(session.user.id, session.user.email || undefined, {
            created_at: session.user.created_at,
          });
        }
      } else if (event === 'SIGNED_OUT') {
        // User and session already set to null above (lines 148, 154)
        setError(null);
        
        // Reset PostHog user identity
        const { resetUser } = await import('../services/posthogService');
        resetUser();
      } else if (event === 'TOKEN_REFRESHED') {
        // Session refreshed, user already updated above (line 152)
        setError(null);
      }

      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array: subscribe once on mount, cleanup on unmount

  const value: AuthContextValue = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user && !!session,
    error,
    signIn,
    signInWithTestCode,
    signOut,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 * 
 * Provides access to authentication context.
 * Must be used within an AuthProvider.
 * 
 * @returns Authentication context value
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

